import React from 'react';
import { Activity, Users, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { formatNumber, formatPercentage, calculateSMRConfidence } from '../utils/saps3Utils';
import LatinAmericaComparison from './LatinAmericaComparison';
import {
  UnimedContainer,
  UnimedGrid,
  UnimedMetricCard,
  UnimedCard,
  UnimedCardHeader,
  UnimedCardTitle,
  UnimedCardContent,
  UnimedTable,
  UnimedTableHeader,
  UnimedTableHeaderCell,
  UnimedTableBody,
  UnimedTableRow,
  UnimedTableCell,
  UnimedAlert,
  UnimedAlertDescription,
  UnimedBadge
} from './ui/UnimedComponents';

const MortalityDashboard = ({ data, stats }) => {
  if (!stats) return null;

  const smrConfidence = calculateSMRConfidence(stats.observedDeaths, stats.expectedDeaths);
  

  const getSMRInterpretation = (smr) => {
    if (smr < 0.8) return 'Mortalidade abaixo do esperado (Bom desempenho)';
    if (smr > 1.2) return 'Mortalidade acima do esperado (Requer atenção)';
    return 'Mortalidade dentro do esperado';
  };

  return (
    <UnimedContainer>
      {/* Executive Summary Cards */}
      <UnimedGrid cols={4}>
        <UnimedMetricCard
          title="Total de Pacientes"
          value={stats.totalPatients.toLocaleString()}
          subtitle="Base da análise"
          icon={Users}
          variant="primary"
        />

        <UnimedMetricCard
          title="Mortalidade Observada"
          value={formatPercentage(stats.observedMortality)}
          subtitle={`${stats.observedDeaths} óbitos · Real`}
          icon={Activity}
          variant="secondary"
        />

        <UnimedMetricCard
          title="Mortalidade Esperada"
          value={formatPercentage(stats.expectedMortality)}
          subtitle={`${formatNumber(stats.expectedDeaths, 1)} óbitos · SAPS3`}
          icon={TrendingUp}
          variant="accent"
        />

        <UnimedMetricCard
          title="SMR · Ratio"
          value={formatNumber(stats.smr, 2)}
          subtitle={`IC 95%: ${formatNumber(smrConfidence.lower, 2)} - ${formatNumber(smrConfidence.upper, 2)}`}
          icon={stats.smr < 0.8 ? TrendingDown : stats.smr > 1.2 ? TrendingUp : AlertTriangle}
          variant={stats.smr < 0.8 ? 'primary' : stats.smr > 1.2 ? 'secondary' : 'neutral'}
        />
      </UnimedGrid>

      {/* SMR Interpretation */}
      <UnimedAlert variant={stats.smr < 0.8 ? 'success' : stats.smr > 1.2 ? 'error' : 'warning'}>
        <UnimedAlertDescription>
          <div className="unimed-titulo-secundario font-medium mb-2">INTERPRETAÇÃO DO SMR</div>
          <p className="unimed-texto-corrido">{getSMRInterpretation(stats.smr)}</p>
          <p className="unimed-texto-corrido text-xs mt-2">
            SMR = 1.0 indica mortalidade igual ao esperado pelo SAPS3. 
            SMR &lt; 1.0 indica performance melhor que o esperado. 
            SMR &gt; 1.0 indica performance pior que o esperado.
          </p>
        </UnimedAlertDescription>
      </UnimedAlert>

      {/* Mortality by SAPS3 Score Ranges */}
      <UnimedCard>
        <UnimedCardHeader>
          <UnimedCardTitle>Mortalidade por Faixas de SAPS3</UnimedCardTitle>
        </UnimedCardHeader>
        <UnimedCardContent className="p-0">
          <UnimedTable>
            <UnimedTableHeader>
              <tr>
                <UnimedTableHeaderCell>Faixa SAPS3</UnimedTableHeaderCell>
                <UnimedTableHeaderCell>Pacientes</UnimedTableHeaderCell>
                <UnimedTableHeaderCell>Mortalidade Observada</UnimedTableHeaderCell>
                <UnimedTableHeaderCell>Mortalidade Esperada</UnimedTableHeaderCell>
                <UnimedTableHeaderCell>SMR</UnimedTableHeaderCell>
              </tr>
            </UnimedTableHeader>
            <UnimedTableBody>
              {stats.mortalityByRange.map((range, index) => (
                <UnimedTableRow key={index} index={index}>
                  <UnimedTableCell variant="bold">{range.range}</UnimedTableCell>
                  <UnimedTableCell>{range.totalPatients}</UnimedTableCell>
                  <UnimedTableCell>{formatPercentage(range.observedMortality)} ({range.observedDeaths})</UnimedTableCell>
                  <UnimedTableCell>{formatPercentage(range.expectedMortality)} ({formatNumber(range.expectedDeaths, 1)})</UnimedTableCell>
                  <UnimedTableCell>
                    <UnimedBadge variant={
                      range.smr < 0.8 ? 'success' : 
                      range.smr > 1.2 ? 'warning' : 
                      'neutral'
                    }>
                      {formatNumber(range.smr, 2)}
                    </UnimedBadge>
                  </UnimedTableCell>
                </UnimedTableRow>
              ))}
            </UnimedTableBody>
          </UnimedTable>
        </UnimedCardContent>
      </UnimedCard>

      {/* Key Performance Indicators */}
      <UnimedCard>
        <UnimedCardHeader>
          <UnimedCardTitle>Indicadores de Performance</UnimedCardTitle>
        </UnimedCardHeader>
        <UnimedCardContent>
          <UnimedGrid cols={3}>
            <div className="text-center p-6 bg-unimed-beige-50 rounded-lg border border-unimed-beige-200">
              <p className="unimed-chapeu text-sm font-medium mb-2 text-unimed-darkgreen-600">DIFERENÇA DE MORTALIDADE</p>
              <p className={`unimed-titulo-principal text-2xl font-bold ${
                stats.observedMortality > stats.expectedMortality ? 'text-unimed-orange-600' : 'text-unimed-green-600'
              }`}>
                {stats.observedMortality > stats.expectedMortality ? '+' : ''}{formatNumber(stats.observedMortality - stats.expectedMortality, 1)}%
              </p>
            </div>
            
            <div className="text-center p-6 bg-unimed-beige-50 rounded-lg border border-unimed-beige-200">
              <p className="unimed-chapeu text-sm font-medium mb-2 text-unimed-darkgreen-600">VIDAS POUPADAS/PERDIDAS</p>
              <p className={`unimed-titulo-principal text-2xl font-bold ${
                stats.observedDeaths > stats.expectedDeaths ? 'text-unimed-orange-600' : 'text-unimed-green-600'
              }`}>
                {stats.observedDeaths > stats.expectedDeaths ? '+' : ''}{formatNumber(stats.observedDeaths - stats.expectedDeaths, 0)}
              </p>
            </div>

            <div className="text-center p-6 bg-unimed-beige-50 rounded-lg border border-unimed-beige-200">
              <p className="unimed-chapeu text-sm font-medium mb-2 text-unimed-darkgreen-600">ACURÁCIA DO MODELO</p>
              <p className="unimed-titulo-principal text-2xl font-bold text-unimed-lightblue-600">
                {formatPercentage(100 - Math.abs(stats.observedMortality - stats.expectedMortality))}
              </p>
            </div>
          </UnimedGrid>
        </UnimedCardContent>
      </UnimedCard>

      {/* Latin America Reference Comparison */}
      <LatinAmericaComparison stats={stats} />
    </UnimedContainer>
  );
};

export default MortalityDashboard;
